/**
 * The frame every page renders inside: sidebar, top bar, page header, the
 * routed content, and the floating "add client" button.
 *
 * The header lives here rather than in each page so that Import and Export
 * always act on the list the user is currently looking at.
 */
import { useEffect, useRef } from 'react';
import { Outlet, useLocation, Navigate } from 'react-router-dom';
import { useApp } from '../store/AppContext.jsx';
import { NAV, menuOfView, homeFor } from '../constants/defaults.js';
import Icon from '../lib/icons.jsx';
import { readClientCSV, exportClientsCSV } from '../lib/csv.js';

import Sidebar from './Sidebar.jsx';
import Topbar from './Topbar.jsx';
import SheetHost from './SheetHost.jsx';
import Toast from './Toast.jsx';
import LockScreen from './LockScreen.jsx';
import PageHeader from './PageHeader.jsx';
import ErrorBoundary from './ErrorBoundary.jsx';

/*
 * Which module a view belongs to. The page frame carries it as a data
 * attribute so a stylesheet can dress one module's screens — the sales stack
 * gets its own treatment — without every shared component growing a prop for
 * it, and without the tiles on Billing changing because Deals wanted a
 * gradient.
 */
const MODULES = {
  sales: ['sales', 'pipeline', 'deals', 'contacts', 'companies', 'activities', 'follow-ups', 'sales-reports'],
  /* Its own inner design - see styles/clients-ui.css. */
  clients: ['dashboard', 'clients', 'trials', 'payments', 'renewals', 'demo-bookings', 'demo-reports'],
  /* See styles/marketing-ui.css. */
  marketing: ['marketing', 'mkt-email', 'mkt-whatsapp', 'mkt-sms', 'mkt-push', 'mkt-journeys',
    'mkt-studio', 'mkt-contacts', 'mkt-audiences', 'mkt-templates']
};

const moduleOf = view =>
  Object.keys(MODULES).find(m => MODULES[m].includes(view)) || null;

/** Map the current path back onto the view key the rest of the app uses. The
    full-page detail routes carry their parent section's view, so they inherit
    its permission and its module styling. */
function viewFromPath(pathname) {
  if (pathname.startsWith('/settings')) return 'settings';
  if (/^\/leads\/.+/.test(pathname)) return 'pipeline';
  if (/^\/clients\/.+/.test(pathname)) return 'clients';
  const match = NAV.find(n => n.path && n.path === pathname);
  return match ? match.v : 'dashboard';
}

/** The full-page detail routes draw their own header card, so the shell's
    page header is suppressed for them. */
const isDetailRoute = pathname => /^\/(leads|clients)\/.+/.test(pathname);

export default function AppShell() {
  const {
    miniNav, navHidden, setNavHidden, locked, S, derive, visible, importClients, toast, setSheet,
    resetFilters, menus, menusReady, can
  } = useApp();
  const { pathname, state } = useLocation();
  const fileInput = useRef(null);

  const view = viewFromPath(pathname);

  /*
   * A page the role cannot open is not shown, even by typing its address:
   * the API refuses the data behind it anyway, and a screen of empty tiles
   * over a "forbidden" toast is worse than being sent somewhere that works.
   * `menus` is what the API granted, and nothing is drawn until it has
   * arrived — see `waiting` below.
   *
   * ...except your own account, which is nobody's to withhold.
   *
   * Settings sits behind the `settings` permission because most of it
   * changes the workspace for everybody. Profile and Appearance do not:
   * they are the signed-in person's own name and their own theme. Gated
   * with the rest, the Profile item on the account menu changed the address
   * and bounced straight back to the dashboard.
   */
  const OWN = ['/settings/profile', '/settings/appearance'];
  const allowed = !menus || OWN.includes(pathname) || can(menuOfView(view), 'view');

  /* And nothing is drawn while the answer is still coming: a page the role
     cannot open used to render in full, then redirect - which on a slow
     connection is a screen of somebody else's data. */
  const waiting = !menusReady;

  /*
   * Leaving a page drops its filters.
   *
   * The four lists share one filter state, so without this a search typed on
   * Clients was still narrowing Renewals a click later - with the panel shut,
   * the only sign was a shorter list. The exception is a link that means to
   * carry a filter with it: a stat tile is the quickest way from Unpaid to
   * Trials precisely because it takes its preset along, so those navigate with
   * `keepFilter` and are left alone.
   */
  const keepFilter = !!state?.keepFilter;
  useEffect(() => {
    if (!keepFilter) resetFilters();
  }, [pathname, keepFilter, resetFilters]);

  const onImportFile = file => {
    const reader = new FileReader();
    reader.onload = () => {
      const { records, error } = readClientCSV(reader.result);
      if (error) { toast(error); return; }
      importClients(records);
    };
    reader.onerror = () => toast('That file could not be read');
    reader.readAsText(file);
  };

  const onExport = () => {
    const n = exportClientsCSV(visible(view), r => derive(r).type, S.dateFmt);
    toast(`Exported ${n} rows`);
  };

  if (!allowed) return <Navigate to={homeFor(menus, S)} replace />;

  return (
    <>
      <div className={`app${miniNav ? ' mini' : ''}${navHidden ? ' closed' : ''}`}>
        <Sidebar />
        <main>
          <Topbar view={view} />
          <div className="page" data-view={view} data-mod={moduleOf(view) || undefined}>
            {isDetailRoute(pathname) ? null : (
              <PageHeader
                view={view}
                onImport={() => fileInput.current?.click()}
                onExport={onExport}
              />
            )}
            {/* The page waits with the rail: rendering a screen the role
                cannot open and redirecting a moment later is, on a slow
                connection, a look at somebody else's data. */}
            {waiting ? (
              <div className="pagewait" aria-busy="true" aria-label="Loading">
                <span /><span /><span />
              </div>
            ) : (
              <ErrorBoundary key={pathname}>
                <Outlet />
              </ErrorBoundary>
            )}
          </div>
        </main>
      </div>

      {/* Tapping the page behind the drawer shuts it — narrow screens only,
          where the rail overlays the content instead of sitting beside it. */}
      {navHidden ? null : (
        <div className="navscrim" role="presentation" onClick={() => setNavHidden(true)} />
      )}

      {/* No floating add button. Every screen already carries the one
          thing it makes - New client, New deal, New campaign - in its own
          header, and a button that hovers over all of them offering a
          fourth thing is a button pressed by accident. */}

      <input
        type="file"
        accept=".csv,text/csv"
        hidden
        ref={fileInput}
        onChange={e => { const f = e.target.files[0]; if (f) onImportFile(f); e.target.value = ''; }}
      />

      <SheetHost />
      <Toast />
      {locked ? <LockScreen /> : null}
    </>
  );
}
